import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ApiService } from "@/services/api/ApiService";
import type { PageDraftState, SavedPageGuide } from "@/types/help-hub/page-draft";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

export const usePageDraft = (agentId: string | undefined, documents: DocumentWithMetadata[] = []) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["genie-page-draft", agentId],
    enabled: Boolean(agentId),
    queryFn: () => ApiService.invoke<PageDraftState>({
      resource: "genie-documents", action: "page-draft", data: { agentId },
    }),
  });
  const savedGuides: SavedPageGuide[] = documents.filter((document) => document.source_type === "genie-document")
    .map((document) => ({ id: document.id, name: document.name, isPublic: document.is_public === true }));
  const hiddenAfterApproval = query.data?.draft?.documents.filter((document) =>
    document.action === "update" && document.previous?.is_public === false).length ?? 0;
  const invalidate = () => Promise.all(
    ["genie-page-draft", "documents", "documents-infinite", "knowledge-base-docs",
      "agent", "agents", "public-help-hub", "public-help-guide"]
      .map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
  );
  useEffect(() => {
    if (!agentId) return;
    const channel = supabase.channel("genie-page-updates")
      .on("broadcast", { event: "draft-changed" }, ({ payload }) => {
        if (payload.agentId === agentId) {
          void queryClient.invalidateQueries({ queryKey: ["genie-page-draft", agentId] });
        }
      }).subscribe((status) => {
        if (status === "SUBSCRIBED") void queryClient.invalidateQueries({ queryKey: ["genie-page-draft", agentId] });
      });
    return () => { void supabase.removeChannel(channel); };
  }, [agentId, queryClient]);

  const generate = useMutation({
    mutationFn: () => ApiService.invoke<{ skipped?: string | unknown[]; provisioned?: unknown[] }>({
      resource: "genie-documents", action: "auto-provision", data: { agentId },
    }),
    onSuccess: (result) => {
      if (result.skipped === "in-progress") toast.info("Your page draft is being prepared");
      else if (result.provisioned?.length) toast.success("Your page draft is ready to review");
      else toast.info("No new guide content was prepared; check your knowledge and any removal suggestions below.");
    },
    onError: () => toast.error("Couldn't prepare your page draft. Please try again."),
    onSettled: invalidate,
  });
  const review = useMutation({
    // Realtime may clear the draft before the mutation resolves; retain the reviewed outcome.
    onMutate: () => ({ hiddenAfterApproval }),
    mutationFn: (decision: "approve" | "dismiss") => ApiService.invoke({
      resource: "genie-documents", action: "review-page-draft",
      data: { agentId, draftId: query.data?.draft?.id, decision },
    }),
    onSuccess: (_, decision, outcome) => {
      if (decision === "dismiss") toast.success("Suggestions dismissed; saved guides are unchanged");
      else if (outcome?.hiddenAfterApproval) toast.success(
        `Changes saved; ${outcome.hiddenAfterApproval} guide${outcome.hiddenAfterApproval === 1 ? " is" : "s are"} still hidden`,
        { description: "Find your work under Saved guides and switch on any guide you want visitors to see." },
      );
      else toast.success("Page content saved", { description: "Your work is available under Saved guides." });
    },
    onError: (error: Error) => toast.error(error.message || "Couldn't review the page draft"),
    onSettled: invalidate,
  });
  return { query, generate, review, savedGuides, hiddenAfterApproval };
};
